// ── Job-Level Spec Drift Detection (Layer 3e — per-job) ──
// Pure, read-only selector. Detects when a SINGLE job has had phase work
// submitted under more than one JobSpec version, or when its submitted
// versions don't match its current bound version.
//
// This is distinct from getSpecDrift() in jobSpecDrift.ts:
//   • getSpecDrift          → fleet-level (v1 vs v2 quality regressions across many jobs)
//   • getJobSpecDrift       → per-job (did THIS job get rebound mid-stream?)
//
// SCOPE GUARDRAILS:
//   - Single job only. Never compares across jobs.
//   - Never reads aggregate telemetry.
//   - No mutations, no persistence, no UI.
//   - Embedded specVersion (on execution + proof events) is preferred;
//     legacy events without attribution fall back to current bound version.

import type { Job } from "./types";
import type { JobSpecPhaseName } from "./jobSpec";
import type { JobSpecExecution } from "./jobSpecExecution";
import type { ProofGapEvent } from "./installerProof";

export interface JobSpecDriftReport {
  jobId: string;
  /** Current bound version on the job (undefined if job is unbound). */
  currentBoundVersion: number | undefined;
  /**
   * Distinct spec versions observed in submitted phase work for this job,
   * sorted ascending. Empty if no phases have been submitted yet.
   */
  submittedUnderVersions: number[];
  /**
   * Phase names that have at least one submit recorded under a version
   * ≠ currentBoundVersion. If currentBoundVersion is undefined, any phase
   * with a submit qualifies.
   */
  affectedPhases: JobSpecPhaseName[];
  /**
   * True when EITHER:
   *   - more than one distinct version appears in submitted work, OR
   *   - the single submitted version mismatches currentBoundVersion.
   * False when no submits exist (don't false-positive freshly-bound jobs).
   */
  hasDrift: boolean;
}

interface JobDriftInputs {
  job: Job;
  specExecutions: JobSpecExecution[];
  proofGapEvents: ProofGapEvent[];
}

/**
 * Resolve the spec version a phase was submitted under.
 *  1. JobSpecExecution.specRef.versionNumber (primary signal)
 *  2. ProofGapEvent.specVersion (supporting evidence on the same phase)
 *  3. Legacy fallback → current bound version (only when no embedded
 *     attribution exists anywhere for that phase submit).
 */
export function getJobSpecDrift(args: JobDriftInputs): JobSpecDriftReport {
  const { job, specExecutions, proofGapEvents } = args;

  const currentBoundVersion = job.boundJobSpec?.versionNumber;
  const currentSpecId = job.boundJobSpec?.specId;

  // ── Collect (phaseName → set of versions) for THIS job only ──
  const phaseVersions = new Map<JobSpecPhaseName, Set<number>>();
  const addObservation = (phase: JobSpecPhaseName, version: number) => {
    if (!phaseVersions.has(phase)) phaseVersions.set(phase, new Set());
    phaseVersions.get(phase)!.add(version);
  };

  // 1. Primary: submitted phase executions for this job.
  // Only count phases that have advanced past not_started — i.e. there is
  // actual installer work attributable to a version. Freshly-bound jobs
  // with zero phase activity must NOT flag drift.
  const ownExecs = specExecutions.filter((e) => e.jobId === job.id);
  for (const exec of ownExecs) {
    // Embedded execution attribution is preferred. Falls back to current
    // bound version only when execution carries no specRef AND job is bound.
    const execVersion =
      exec.specRef?.versionNumber ??
      (currentBoundVersion !== undefined && exec.specRef?.specId === undefined
        ? currentBoundVersion
        : undefined);

    if (execVersion === undefined) continue;

    for (const phase of exec.phases) {
      // "Submitted under" means installer has committed work to this phase.
      if (phase.status === "not_started") continue;
      addObservation(phase.phaseName, execVersion);
    }
  }

  // 2. Supporting: proof-gap events for this job carry per-event specVersion.
  // Useful when an event was emitted under v1 but the exec snapshot was
  // later overwritten or moved. Legacy events without specVersion fall
  // back to current bound version.
  const ownGaps = proofGapEvents.filter((e) => e.jobId === job.id);
  for (const gap of ownGaps) {
    const sameSpec =
      gap.specId === undefined || // legacy — accept under current spec
      currentSpecId === undefined ||
      gap.specId === currentSpecId;
    if (!sameSpec) continue;

    const gapVersion = gap.specVersion ?? currentBoundVersion;
    if (gapVersion === undefined) continue;

    addObservation(gap.phaseId as JobSpecPhaseName, gapVersion);
  }

  // ── Reduce ──
  const allVersions = new Set<number>();
  const affectedPhases: JobSpecPhaseName[] = [];

  for (const [phase, versions] of phaseVersions) {
    for (const v of versions) allVersions.add(v);
    const phaseHasMismatch =
      currentBoundVersion === undefined
        ? versions.size > 0
        : Array.from(versions).some((v) => v !== currentBoundVersion) ||
          versions.size > 1;
    if (phaseHasMismatch) affectedPhases.push(phase);
  }

  const submittedUnderVersions = Array.from(allVersions).sort((a, b) => a - b);

  // hasDrift requires actual submitted work. Empty submits → no drift.
  let hasDrift = false;
  if (submittedUnderVersions.length > 1) {
    hasDrift = true;
  } else if (
    submittedUnderVersions.length === 1 &&
    currentBoundVersion !== undefined &&
    submittedUnderVersions[0] !== currentBoundVersion
  ) {
    hasDrift = true;
  }

  return {
    jobId: job.id,
    currentBoundVersion,
    submittedUnderVersions,
    affectedPhases,
    hasDrift,
  };
}
